import { useEffect, useRef } from 'react';

import _ from 'lodash';

import { loanInfoConfigFields } from '../LoanInfo.config';
import { DOWN_PAYMENT_SYNC_SOURCE, DownPaymentFieldName } from '../LoanInfo.constants';
import {
  getDownPaymentSyncSource,
  hasFieldChanged,
  resolveChattelDownPaymentSync,
} from '../LoanInfo.utils';

// LEN-127: a chattel sales contract keeps base = contract - down payment whichever of $, % or base the user
// drove (on open the down payment is read back off the base), and shows the Total Loan Amount as the base
// plus the costs the backend financed on top of it.
export const useChattelDownPaymentSync = ({
  isChattelSalesContract,
  contractAmount,
  totalSalesPrice,
  financedCosts,
  downPaymentValue,
  downPaymentPercent,
  formLoanAmount,
  formTotalLoanAmount,
  setMultipleInForm,
}) => {
  const chattelSyncRef = useRef(null);

  useEffect(() => {
    if (!isChattelSalesContract) {
      chattelSyncRef.current = null;

      return;
    }

    const prevSync = chattelSyncRef.current;

    // The form mounts before its initial values land, and Content.js merges them UNDER what the form already
    // holds, so anything written now would override the saved values. Start once the base loan amount is in.
    if (!prevSync && _.isNil(formLoanAmount)) {
      return;
    }

    const hasDownPaymentValueChanged =
      !!prevSync && hasFieldChanged(prevSync.downPaymentValue, downPaymentValue);
    const hasDownPaymentPercentChanged =
      !!prevSync && hasFieldChanged(prevSync.downPaymentPercent, downPaymentPercent);
    const hasLoanAmountChanged = !!prevSync && prevSync.baseLoanAmount !== formLoanAmount;
    const hasBasisChanged =
      !!prevSync &&
      (prevSync.contractAmount !== contractAmount || prevSync.totalSalesPrice !== totalSalesPrice);

    if (
      prevSync &&
      !hasDownPaymentValueChanged &&
      !hasDownPaymentPercentChanged &&
      !hasLoanAmountChanged &&
      !hasBasisChanged
    ) {
      return;
    }

    const source = getDownPaymentSyncSource({
      hasDownPaymentPercentChanged,
      hasDownPaymentValueChanged,
    });
    const nextSync = resolveChattelDownPaymentSync({
      source,
      contractAmount,
      totalSalesPrice,
      downPaymentValue,
      downPaymentPercent,
      baseLoanAmount: formLoanAmount,
    });
    // A cleared down payment leaves the base loan amount as it is rather than blanking it.
    const drivesLoanAmount =
      (source === DOWN_PAYMENT_SYNC_SOURCE.PERCENT ||
        source === DOWN_PAYMENT_SYNC_SOURCE.DOWN_PAYMENT) &&
      !_.isNil(nextSync.baseLoanAmount);
    const nextBaseLoanAmount = drivesLoanAmount ? nextSync.baseLoanAmount : formLoanAmount;
    const nextTotalLoanAmount = _.isNil(nextBaseLoanAmount)
      ? undefined
      : _.round(Number(nextBaseLoanAmount) + financedCosts, 2);

    const updates = {};

    if (nextSync.downPaymentValue !== downPaymentValue) {
      updates[DownPaymentFieldName.AMOUNT] = nextSync.downPaymentValue;
    }

    if (nextSync.downPaymentPercent !== downPaymentPercent) {
      updates[DownPaymentFieldName.PERCENT] = nextSync.downPaymentPercent;
    }

    if (drivesLoanAmount && nextBaseLoanAmount !== formLoanAmount) {
      updates[loanInfoConfigFields.baseLoanAmount.nameInModel] = nextBaseLoanAmount;
    }

    if (nextTotalLoanAmount !== formTotalLoanAmount) {
      updates[loanInfoConfigFields.totalLoanAmount.nameInModel] = nextTotalLoanAmount;
    }

    chattelSyncRef.current = {
      contractAmount,
      totalSalesPrice,
      downPaymentValue: nextSync.downPaymentValue,
      downPaymentPercent: nextSync.downPaymentPercent,
      baseLoanAmount: nextBaseLoanAmount,
    };

    if (!_.isEmpty(updates)) {
      setMultipleInForm(updates, false);
    }
  }, [
    isChattelSalesContract,
    contractAmount,
    totalSalesPrice,
    financedCosts,
    downPaymentValue,
    downPaymentPercent,
    formLoanAmount,
    formTotalLoanAmount,
    setMultipleInForm,
  ]);
};
